import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../../app/providers/AuthProvider';
import { COLORS } from '../../../core/constants/colors';
import { api } from '../../../core/utils/apiClient';

interface UserSidebarLayoutProps {
  children: React.ReactNode;
}

interface Session {
  session_id: string;
  title?: string;
  created_at: string;
}

interface MyCompany {
  id: number;
  name: string;
}

export const UserSidebarLayout: React.FC<UserSidebarLayoutProps> = ({ children }) => {
  const { authState, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [sessions, setSessions] = useState<Session[]>([]);
  const [sessionsLoading, setSessionsLoading] = useState(true);
  const [myCompanies, setMyCompanies] = useState<MyCompany[]>([]);
  const [myCompaniesLoaded, setMyCompaniesLoaded] = useState(false);
  const [faqs, setFaqs] = useState<Array<{ question: string; answer: string; open: boolean }>>([
    {
      question: "How does the AI-powered portfolio analysis work?",
      answer: "Our system uses advanced natural language processing to analyze your portfolio documents, extract key insights, and provide intelligent responses to your queries about company performance, financials, and trends.",
      open: false
    },
    {
      question: "What types of documents can I upload?",
      answer: "You can upload financial reports, pitch decks, business plans, legal documents, and any PDF files related to your portfolio companies. Our system automatically indexes and processes them for quick retrieval.",
      open: false
    },
    {
      question: "How secure is my portfolio data?",
      answer: "All data is encrypted at rest and in transit. We use enterprise-grade security measures including AWS S3 for storage, role-based access control, and regular security audits to protect your sensitive information.",
      open: false
    },
    {
      question: "Can I track multiple companies simultaneously?",
      answer: "Yes! The platform is designed to manage multiple portfolio companies. You can filter queries by specific companies, compare metrics across your portfolio, and get consolidated insights.",
      open: false
    }
  ]);

  useEffect(() => {
    loadSessions();
  }, [location.pathname]);

  useEffect(() => {
    loadMyCompanies();
  }, []);

  const loadSessions = async () => {
    setSessionsLoading(true);
    try {
      const response = await api.get('/api/v1/chat/conversations');
      if (response.ok) {
        const data = await response.json();
        const convos = (data.conversations || data || []).map((c: any) => ({
          session_id: String(c.id),
          title: c.title || 'Untitled Chat',
          created_at: c.updated_at || c.created_at,
        }));
        setSessions(convos);
      }
    } catch (error) {
      console.error('Error loading sessions:', error);
    } finally {
      setSessionsLoading(false);
    }
  };

  const loadMyCompanies = async () => {
    try {
      const response = await api.get('/api/v1/companies/my-companies');
      if (response.ok) {
        const data = await response.json();
        const list: MyCompany[] = (Array.isArray(data) ? data : (data.companies || [])).map(
          (c: any) => ({ id: c.id as number, name: c.name as string })
        );
        setMyCompanies(list);
      }
    } catch (error) {
      // Silently fall back to list route on any error
      console.error('Error loading my companies:', error);
    } finally {
      setMyCompaniesLoaded(true);
    }
  };

  const handleMyCompaniesClick = useCallback(() => {
    if (myCompaniesLoaded && myCompanies.length === 1) {
      navigate(`/dashboard/companies/${myCompanies[0].id}`);
    } else {
      navigate('/dashboard/user/companies');
    }
    setSidebarOpen(false);
  }, [myCompaniesLoaded, myCompanies, navigate]);

  const isMyCompaniesActive = () => {
    return location.pathname === '/dashboard/user/companies' ||
      (myCompaniesLoaded && myCompanies.length === 1 &&
        location.pathname === `/dashboard/companies/${myCompanies[0].id}`);
  };

  const getUserInitial = () => {
    return authState.user?.full_name?.charAt(0).toUpperCase() || authState.user?.email?.charAt(0).toUpperCase() || 'U';
  };

  const isActive = (path: string) => {
    if (path === '/dashboard' || path === '/dashboard/query') {
      return location.pathname === '/dashboard' || location.pathname === '/dashboard/query';
    }
    return location.pathname.startsWith(path);
  };

  return (
    <div className="min-h-screen flex" style={{ fontFamily: 'DM Sans, sans-serif', backgroundColor: '#FAFAFA' }}>
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 ${sidebarCollapsed ? 'w-16' : 'w-64'} transform transition-all duration-300 ease-in-out lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        style={{ backgroundColor: '#FAFAFA' }}
      >
        <div className="h-full flex flex-col">
          {/* Logo */}
          <div className="p-4 border-b" style={{ borderColor: '#E5E7EB' }}>
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-3 flex-1">
                <img
                  src="/new_logo_blue.png"
                  alt="Portfolio Indexing"
                  className="h-12 cursor-pointer"
                  onClick={() => navigate('/dashboard')}
                  onError={(e) => {
                    e.currentTarget.src = '/pi_logo.png';
                  }}
                />
              </div>
              <button
                onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                className="p-1.5 rounded hover:bg-gray-100 transition-all text-gray-600 hover:text-gray-900"
                title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={sidebarCollapsed ? "M9 5l7 7-7 7" : "M15 19l-7-7 7-7"} />
                </svg>
              </button>
              <button
                onClick={() => setSidebarOpen(false)}
                className="lg:hidden p-1.5 rounded text-gray-500 hover:bg-gray-100"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {!sidebarCollapsed && (
              <button
                onClick={() => {
                  if (location.pathname === '/dashboard/chat') {
                    navigate('/dashboard/chat', { replace: true, state: { newChat: Date.now() } });
                  } else {
                    navigate('/dashboard/chat');
                  }
                  setSidebarOpen(false);
                }}
                className="w-full mt-4 py-2.5 px-4 rounded-lg font-medium text-sm transition-all flex items-center justify-center gap-2 text-white"
                style={{ backgroundColor: '#1F1F1F', fontFamily: 'DM Sans, sans-serif' }}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                New chat
              </button>
            )}
          </div>

          {/* Search */}
          {!sidebarCollapsed && (
            <div className="px-4 mb-2">
              <div className="relative">
                <svg
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <input
                  type="text"
                  placeholder="Search"
                  className="w-full pl-9 pr-4 py-2 text-sm rounded-lg border-0 focus:outline-none focus:ring-1 focus:ring-gray-300"
                  style={{ backgroundColor: '#F0F0F0' }}
                />
              </div>
            </div>
          )}

          {/* Navigation */}
          <nav className="flex-1 px-4 py-2 overflow-y-auto" style={{ fontFamily: 'DM Sans, sans-serif' }}>
            {!sidebarCollapsed ? (
              <>
                <div className="space-y-0.5">
                  <button
                    onClick={() => {
                      navigate('/dashboard/companies');
                      setSidebarOpen(false);
                    }}
                    className={`w-full flex items-center gap-3 px-3 py-2 text-sm transition-all rounded-md ${
                      isActive('/dashboard/companies') && !isMyCompaniesActive()
                        ? 'text-white'
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                    style={isActive('/dashboard/companies') && !isMyCompaniesActive() ? { backgroundColor: COLORS.user.primary } : {}}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4a2 2 0 012-2h8a2 2 0 012 2v12a1 1 0 110 2h-3a1 1 0 01-1-1v-2a1 1 0 00-1-1H9a1 1 0 00-1 1v2a1 1 0 01-1 1H4a1 1 0 110-2V4zm3 1h2v2H7V5zm2 4H7v2h2V9zm2-4h2v2h-2V5zm2 4h-2v2h2V9z" />
                    </svg>
                    <span>Companies</span>
                  </button>

                  <button
                    onClick={handleMyCompaniesClick}
                    className={`w-full flex items-center gap-3 px-3 py-2 text-sm transition-all rounded-md ${
                      isMyCompaniesActive()
                        ? 'text-white'
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                    style={isMyCompaniesActive() ? { backgroundColor: COLORS.user.primary } : {}}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    <span>My Companies</span>
                  </button>

                  <button
                    onClick={() => {
                      navigate('/dashboard/history');
                      setSidebarOpen(false);
                    }}
                    className={`w-full flex items-center gap-3 px-3 py-2 text-sm transition-all rounded-md ${
                      isActive('/dashboard/history')
                        ? 'text-white'
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                    style={isActive('/dashboard/history') ? { backgroundColor: COLORS.user.primary } : {}}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span>History</span>
                  </button>

                  <button
                    onClick={() => {
                      navigate('/dashboard/billing');
                      setSidebarOpen(false);
                    }}
                    className={`w-full flex items-center gap-3 px-3 py-2 text-sm transition-all rounded-md ${
                      isActive('/dashboard/billing')
                        ? 'text-white'
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                    style={isActive('/dashboard/billing') ? { backgroundColor: COLORS.user.primary } : {}}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                    </svg>
                    <span>Billing</span>
                  </button>

                </div>

                {/* Previous Chats or FAQs */}
                {!sidebarCollapsed && (
                  <div className="mt-6">
                    {sessions.length > 0 ? (
                      <>
                        <div className="px-3 mb-2">
                          <p className="text-xs font-medium text-gray-500">Recent Chats</p>
                        </div>
                        <div className="space-y-0.5">
                          {sessions.slice(0, 5).map((session) => (
                            <button
                              key={session.session_id}
                              onClick={() => {
                                navigate(`/dashboard/chat/${session.session_id}`);
                                setSidebarOpen(false);
                              }}
                              className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md truncate"
                            >
                              {session.title || 'Untitled Chat'}
                            </button>
                          ))}
                        </div>
                      </>
                    ) : !sessionsLoading ? (
                      <>
                        <div className="px-3 mb-2">
                          <p className="text-xs font-medium text-gray-500">FAQs</p>
                        </div>
                        <div className="space-y-1">
                          {faqs.map((faq, index) => (
                            <div key={index}>
                              <button
                                onClick={() => {
                                  const newFaqs = [...faqs];
                                  newFaqs[index].open = !newFaqs[index].open;
                                  setFaqs(newFaqs);
                                }}
                                className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md"
                              >
                                <div className="flex items-center justify-between">
                                  <span className={`pr-2 ${faq.open ? '' : 'truncate'}`}>{faq.question}</span>
                                  <svg
                                    className={`w-4 h-4 flex-shrink-0 transition-transform ${faq.open ? 'rotate-180' : ''}`}
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                  </svg>
                                </div>
                              </button>
                              {faq.open && (
                                <div className="px-3 py-2 text-xs text-gray-600 bg-gray-50 rounded-md mt-1">
                                  <div className="font-medium text-gray-700 mb-1">{faq.question}</div>
                                  <div>{faq.answer}</div>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </>
                    ) : null}
                  </div>
                )}
              </>
            ) : (
              <div className="space-y-2">
                <button
                  onClick={() => {
                    navigate('/dashboard/companies');
                    setSidebarOpen(false);
                  }}
                  className="w-full p-2 rounded-md hover:bg-gray-100 transition-all"
                  title="Companies"
                >
                  <svg className="w-5 h-5 mx-auto text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4a2 2 0 012-2h8a2 2 0 012 2v12a1 1 0 110 2h-3a1 1 0 01-1-1v-2a1 1 0 00-1-1H9a1 1 0 00-1 1v2a1 1 0 01-1 1H4a1 1 0 110-2V4zm3 1h2v2H7V5zm2 4H7v2h2V9zm2-4h2v2h-2V5zm2 4h-2v2h2V9z" />
                  </svg>
                </button>
                <button
                  onClick={handleMyCompaniesClick}
                  className="w-full p-2 rounded-md hover:bg-gray-100 transition-all"
                  title="My Companies"
                >
                  <svg className="w-5 h-5 mx-auto text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </button>
                <button
                  onClick={() => {
                    navigate('/dashboard/history');
                    setSidebarOpen(false);
                  }}
                  className="w-full p-2 rounded-md hover:bg-gray-100 transition-all"
                  title="History"
                >
                  <svg className="w-5 h-5 mx-auto text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </button>
                <button
                  onClick={() => {
                    navigate('/dashboard/billing');
                    setSidebarOpen(false);
                  }}
                  className="w-full p-2 rounded-md hover:bg-gray-100 transition-all"
                  title="Billing"
                >
                  <svg className="w-5 h-5 mx-auto text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                  </svg>
                </button>
              </div>
            )}
          </nav>

          {/* User section */}
          <div className="m-4 rounded-lg" style={{ backgroundColor: '#FFFFFF', fontFamily: 'DM Sans, sans-serif' }}>
            <div
              onClick={() => {
                navigate('/dashboard/user/companies');
                setSidebarOpen(false);
              }}
              className={`w-full flex items-center gap-3 p-2 hover:bg-gray-100 rounded-lg transition-all cursor-pointer ${sidebarCollapsed ? 'justify-center' : ''}`}
            >
              <div
                className="w-8 h-8 rounded-full flex items-center justify-center font-semibold text-xs flex-shrink-0"
                style={{
                  backgroundColor: COLORS.user.primary,
                  color: '#FFFFFF'
                }}
              >
                {getUserInitial()}
              </div>
              {!sidebarCollapsed && (
                <>
                  <div className="flex-1 min-w-0 text-left">
                    <p className="text-gray-900 font-medium text-xs truncate">
                      {authState.user?.full_name || 'User'}
                    </p>
                    <p className="text-gray-500 text-xs truncate">
                      {authState.user?.email}
                    </p>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      logout();
                    }}
                    className="p-1 hover:bg-gray-200 rounded transition-all"
                    title="Logout"
                  >
                    <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                    </svg>
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 transition-all duration-300" style={{ paddingLeft: sidebarCollapsed ? '64px' : '256px' }}>
        {/* Mobile menu button */}
        <div className="lg:hidden fixed top-4 left-4 z-40">
          <button
            onClick={() => setSidebarOpen(true)}
            className="p-2 rounded-lg bg-white shadow-md text-gray-600 hover:text-gray-900"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
        </div>

        {/* Page content */}
        <main className="min-h-screen">
          {children}
        </main>
      </div>
    </div>
  );
};
